<template>
  <div
    class="studio-media-card"
    v-bind="$attrs"
    v-tooltip="
      media.media.locked &&
      !canViewProductContent && {
        content: getCantViewProductReasonText,
        delay: 100,
        placement: 'top',
        container: 'body',
      }
    "
  >
    <HMFixedAspectRatioContainer>
      <MediaThumbnail
        :media="media"
        :isLocked="media.media.locked && !canViewProductContent"
        :mediaIconsEnabled="mediaIconsEnabled"
        :mediaTimeEnabled="false"
      />
    </HMFixedAspectRatioContainer>
    <div class="outer-container d-flex flex-column align-items-start">
      <div class="description flex-grow-1 text-truncate mt-2">
        <div
          class="d-flex flex-column justify-content-between align-items-start"
        >
          <div class="d-flex flex-row align-items-center justify-content-start">
            <div class="d-flex">
              <div
                class="d-flex align-items-center justify-content-start flex-wrap text-truncate mw-100"
              >
                <HMTag
                  class="smallest me-2 mb-1"
                  :title="tag.option_name"
                  v-for="tag in getMediaTags"
                  :key="tag.id"
                />
                <HMTag
                  class="smallest mb-1"
                  v-if="getRemainingMediaTagsText"
                  :title="getRemainingMediaTagsText"
                  v-tooltip="{
                    content: getCutOffMediaTagsHtml,
                    html: true,
                    delay: 100,
                    offset: 5,
                    placement: 'bottom',
                    container: 'body',
                  }"
                />
              </div>
            </div>
          </div>
          <div
            class="d-flex flex-row align-items-start justify-content-start mt-2"
          >
            <HMInstructorsStack
              class="me-2"
              v-if="media.instructor"
              :instructors="getInstructors(media.instructor)"
              size="24px"
            />
            <div class="media-name-container m-0">
              <HMTextClamp
                autoresize
                :max-lines="2"
                :text="media.title"
              />
              <slot></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import _ from 'lodash-es';
import moment from '@/services/moment';
import { mapState } from 'pinia';
import { formatDate } from '@/utils/format';
import MediaThumbnail from '@/components/products/MediaThumbnail.vue';

import { useStudioStore, useProductStore } from '@/stores';

export default {
  name: 'StudioMediaCard',
  components: { 
    MediaThumbnail
  },
  props: {
    product: {
      type: Object as () => ProductResponse,
      required: false,
    },
    media: {
      type: Object as () => ProductMediaResponse,
      required: true,
    },
  },
  computed: {
    ...mapState(useStudioStore, {
      mediaIconsEnabled: 'mediaIconsEnabled',
    }),
    ...mapState(useProductStore, {
      cantViewProductContentReason: 'cantViewProductContentReason',
      canViewProductContentState: 'canViewProductContent',
    }),
    canViewProductContent() {
        if (!this.product) return true;
        return this.canViewProductContentState
    },
    cantViewBecauseNotPurchased() {
      return this.cantViewProductContentReason === 'not_purchased';
    },
    getCantViewProductReasonText() {
      if (!this.product) return '';
      if (this.canViewProductContent) return null;
      switch (this.cantViewProductContentReason) {
        case 'unauthenticated':
          return this.$t(
            'PRODUCTS.TEXT.PRODUCT_NO_ACCESS_USER_NOT_AUTHENTICATED'
          );
        case 'not_purchased':
          return this.$t('PRODUCTS.TEXT.PRODUCT_NO_ACCESS_NOT_PURCHASED');
        case 'paused':
          return this.$t('PRODUCTS.TEXT.PRODUCT_NO_ACCESS_PAUSED');
        case 'availability_expired':
          return this.$t('PRODUCTS.TEXT.PRODUCT_NO_ACCESS_EXPIRED', {
            date: formatDate(
              this.product.can_view_content.end_date,
              'date-time'
            ),
          });
        case 'drip_expired':
          return this.$t('PRODUCTS.TEXT.PRODUCT_NO_ACCESS_EXPIRED', {
            date: formatDate(
              this.product.can_view_content.end_date,
              'date-time'
            ),
          });
        case 'not_yet_available':
          return this.$t('PRODUCTS.TEXT.PRODUCT_NO_ACCESS_NOT_YET_AVAILABLE', {
            date: formatDate(
              this.product.can_view_content.start_date,
              'date-time'
            ),
          });
      }
      return '';
    },
    getMediaTags() {
      const mediaOptionsArray = this.media.options;
      const sortedMediaOptionsArray = mediaOptionsArray
        .sort((o1, o2) => o1.category_position - o2.category_position)
        .slice();
      return sortedMediaOptionsArray.splice(0, 2);
    },

    getCutOffMediaTagsHtml() {
      const mediaOptionsArray = this.media.options.slice();
      return mediaOptionsArray
        .splice(2)
        .map(option => option.option_name)
        .join('<br/>');
    },
    getRemainingMediaTagsText() {
      if (this.media.options.length < 3) return null;
      return `+ ${this.media.options.length - 2}`;
    },
  },
  methods: {
    getInstructors(instructor: ProductInstructorResponse) {
      return [instructor];
    },
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';
.studio-media-card {
  &:hover {
    :deep(.image) {
      box-shadow: 0 12px 12px -6px $dark-gray;
      transform: translateY(-16px);
    }
    .media-name-container {
      color: var(--brand-color);
    }
  }

  cursor: pointer;
  position: relative;
  border-radius: 6px;

  .outer-container {
    border-radius: 6px;
  }

  .description {
    background-color: $white;
    position: relative;
    bottom: 0;
    left: 0;
    border-top-right-radius: 6px;
    width: 100%;
  }

  .media-name-container {
    white-space: normal;
    min-height: 60px;
    line-height: 1.5;
    font-weight: 500;
    color: $charcoal;
  }
  .heart {
    color: $coral;
  }
  .dot {
    margin-right: 0.25rem;
    margin-top: 0.3rem;
    font-size: 0.5rem;
  }
  .embed-responsive {
    overflow: visible;
  }
}
</style>
